import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MapPin, X } from 'lucide-react';
import { alertIcon } from './alertIcons';
import baseapi, { API_BASE } from '../../baseapi';

/**
 * A small pop-up from the bottom for every new alert, with its description (15 Oct 2026). Shown on every page.
 * An alert that waits for acknowledgement stays on screen until someone acknowledges it (the backend keeps an alert
 * open until it is acknowledged), and comes back after a page reload. Its Dismiss button (15 Oct 2026, operators) hides
 * it on this screen only - it stays open on the Alerts page - and it pops up again if it becomes more serious.
 * "View & acknowledge" opens the Alerts page on that alert's camera pictures. No pop-ups on the Alerts page itself
 * (they would cover the pictures; the page lists every waiting alert). "Darshan resumed"
 * waits too (operators, 15 Oct 2026: it went after a few seconds and was missed): it has its own Acknowledge button
 * and no close. Other information lines go after 12 s and pop up once per browser (on the very first visit only
 * those from the last 15 minutes).
 */
interface Toast {
  id: number; severity: string; title: string; message: string | null; place: string | null;
  opened_at: number; trial: boolean; snaps?: string[]; type?: string; key?: string;
  state?: string; ack_at?: number | null; needs_ack?: boolean;
}
const SEEN_KEY = 'alert_toasts_seen';
const CLOSED_KEY = 'alert_toasts_closed';      // alert id -> severity when its pop-up was closed on this screen
const MAX_SHOWN = 3;
const LIFE_MS: Record<string, number> = { info: 12000 };

const hm = (t: number) => new Date(t * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });

function readSeen(): number[] | null {
  try { const v = localStorage.getItem(SEEN_KEY); return v ? JSON.parse(v) : null; } catch { return null; }
}
function writeSeen(ids: number[]) {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify(ids.slice(-300))); } catch { /* not remembered */ }
}
function readClosed(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(CLOSED_KEY) || '{}'); } catch { return {}; }
}
// waits for somebody to acknowledge it: an open alert, or an information line that asks for it (darshan resumed)
const pending = (a: Toast) => !!a.needs_ack && !a.ack_at && (a.state === 'open' || a.severity === 'info');
// an information line that must be acknowledged: no close, no dismiss
const mustAck = (a: Toast) => a.severity === 'info' && !!a.needs_ack;

export const AlertToasts: React.FC = () => {
  const navigate = useNavigate();
  const onAlertsPage = /\/alerts\/?$/.test(useLocation().pathname);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const closed = useRef<Record<string, string>>(readClosed());
  const seen = useRef<number[] | null>(readSeen());
  const timers = useRef<Map<number, number>>(new Map());
  const [acking, setAcking] = useState<number | null>(null);

  const close = (id: number) => {
    setToasts(t => t.filter(x => x.id !== id));
    const tm = timers.current.get(id);
    if (tm) { window.clearTimeout(tm); timers.current.delete(id); }
  };
  // dismiss: hidden on this screen until the alert is acknowledged, or comes back if its severity changes
  const exit = (a: Toast) => {
    const next: Record<string, string> = { ...closed.current, [a.id]: a.severity };
    closed.current = Object.fromEntries(Object.entries(next).slice(-300));
    try { localStorage.setItem(CLOSED_KEY, JSON.stringify(closed.current)); } catch { /* this session only */ }
    close(a.id);
  };
  // straight to this alert's camera pictures (and its acknowledgement) on the Alerts page
  const view = (a: Toast) => navigate(`/alerts?alert=${a.id}`);
  const acknowledge = async (id: number) => {
    setAcking(id);
    try { await baseapi.post(`alerts/${id}/ack`, { action: '', note: '' }); close(id); }
    catch { /* stays; try again */ }
    finally { setAcking(null); }
  };

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await baseapi.get('alerts?state=all&hours=24');
        if (!alive) return;
        const list: Toast[] = r.data?.alerts || [];
        const firstVisit = seen.current === null;
        const known = new Set(seen.current || []);
        const now = Date.now() / 1000;
        // on screen until acknowledged (shown again after a reload)
        const waiting = list.filter(a => pending(a) && closed.current[a.id] !== a.severity);
        // the rest pops up once, when new, and goes by itself
        const fresh = list
          .filter(a => !pending(a) && !known.has(a.id) && now - a.opened_at < (firstVisit ? 15 : 60) * 60)
          .filter(a => a.severity === 'info')
          .sort((a, b) => a.opened_at - b.opened_at);
        seen.current = [...(seen.current || []), ...list.map(a => a.id).filter(id => !known.has(id))];
        writeSeen(seen.current);
        setToasts(t => {
          const timed = t.filter(x => !pending(x) && !waiting.some(w => w.id === x.id) && LIFE_MS[x.severity]);
          const add = fresh.filter(f => !timed.some(x => x.id === f.id));
          return [...waiting, ...timed, ...add].sort((a, b) => a.opened_at - b.opened_at);
        });
        fresh.forEach(f => {
          const life = LIFE_MS[f.severity];
          if (life && !timers.current.has(f.id)) timers.current.set(f.id, window.setTimeout(() => close(f.id), life));
        });
      } catch { /* try again on the next round */ }
    };
    load();
    const t = window.setInterval(() => { if (!document.hidden) load(); }, 10000);
    return () => { alive = false; window.clearInterval(t); timers.current.forEach(x => window.clearTimeout(x)); };
  }, []);

  if (!toasts.length || onAlertsPage) return null;
  // newest at the bottom, closest to where it slid in from
  const shown = toasts.slice(-MAX_SHOWN);
  const hidden = toasts.length - shown.length;

  return (
    <div className="fixed bottom-4 right-4 z-[70] flex flex-col gap-2 w-[min(420px,calc(100vw-2rem))] pointer-events-none">
      <style>{`@keyframes alertToastUp{from{transform:translateY(24px);opacity:0}to{transform:none;opacity:1}}@keyframes alertToastLife{from{width:100%}to{width:0}}`}</style>
      {hidden > 0 && (
        <button onClick={() => navigate('/alerts')}
          className="pointer-events-auto self-end px-3 py-1 rounded-full text-xs font-bold bg-neutral-900 text-white dark:bg-white dark:text-neutral-900 shadow cursor-pointer">
          +{hidden} more alert{hidden > 1 ? 's' : ''}
        </button>
      )}
      {shown.map(a => {
        const crit = a.severity === 'critical', warn = a.severity === 'warning';
        // 15 Oct 2026 redesign: a coloured header band says how serious it is, a camera picture when there is one
        const band = crit ? 'bg-[#BE123C] text-white' : warn ? 'bg-amber-400 text-amber-950' : 'bg-[#4D8DBA] text-white';
        const label = crit ? 'Critical alert' : warn ? 'Warning' : mustAck(a) ? 'Please acknowledge' : 'Information';
        const life = pending(a) ? undefined : LIFE_MS[a.severity];
        const thumb = (a.snaps || []).slice(-1)[0];
        return (
          <div key={a.id} role="alert"
            style={{ animation: 'alertToastUp .25s ease-out' }}
            className="pointer-events-auto rounded-2xl overflow-hidden bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#2b3140] shadow-[0_14px_40px_rgba(0,0,0,0.22)]">
            <div className={`flex items-center gap-2 px-3 py-1.5 ${band}`}>
              {React.createElement(alertIcon(a.type || '', a.key, a.severity), { className: 'w-4 h-4 shrink-0' })}
              <span className="text-[12px] font-black uppercase tracking-wider">{label}</span>
              {a.trial && <span className="px-1.5 rounded text-[10px] font-bold bg-white/25">Trial</span>}
              <span className="ml-auto text-[12px] font-bold opacity-90">{hm(a.opened_at)}</span>
              {!mustAck(a) && (
                <button onClick={() => (pending(a) ? exit(a) : close(a.id))} aria-label="Close"
                  title={pending(a) ? 'Close on this screen (the alert stays open on the Alerts page)' : 'Close'}
                  className="p-0.5 -mr-1 rounded hover:bg-black/10 cursor-pointer"><X className="w-4 h-4" /></button>
              )}
            </div>
            <div className="flex gap-3 p-3">
              {thumb && (
                <button onClick={() => view(a)} title="Open the camera pictures" className="shrink-0 cursor-pointer self-start">
                  <img src={`${API_BASE}${thumb}`} alt="Camera picture"
                    className="w-[104px] h-[60px] object-cover rounded-lg border border-neutral-200 dark:border-[#2b3140] hover:ring-2 hover:ring-[#761D20]/40" />
                </button>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-black text-neutral-900 dark:text-neutral-50 leading-snug">{a.title}</div>
                {a.message && <div className="text-[13px] text-neutral-700 dark:text-neutral-300 leading-snug mt-0.5 line-clamp-3">{a.message}</div>}
                {a.place && (
                  <div className="mt-1 flex items-center gap-1 text-[12px] font-semibold text-neutral-600 dark:text-neutral-400">
                    <MapPin className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{a.place}</span>
                  </div>
                )}
              </div>
            </div>
            {mustAck(a) ? (
              <div className="flex items-center gap-2 px-3 pb-3">
                <button onClick={() => acknowledge(a.id)} disabled={acking === a.id}
                  className="flex-1 px-3 py-1.5 rounded-lg bg-[#761D20] dark:bg-orange-500 text-white text-sm font-bold cursor-pointer hover:brightness-110 disabled:opacity-60">
                  {acking === a.id ? 'Acknowledging…' : 'Acknowledge'}
                </button>
                <button onClick={() => navigate('/alerts')}
                  className="px-3 py-1.5 rounded-lg text-sm font-bold text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-white/5 hover:bg-neutral-200 dark:hover:bg-white/10 cursor-pointer">
                  View alerts
                </button>
              </div>
            ) : a.severity !== 'info' && (
              <div className="flex items-center gap-2 px-3 pb-3">
                <button onClick={() => view(a)}
                  className="flex-1 px-3 py-1.5 rounded-lg bg-[#761D20] dark:bg-orange-500 text-white text-sm font-bold cursor-pointer hover:brightness-110">
                  View &amp; acknowledge
                </button>
                <button onClick={() => exit(a)} title="Close on this screen (the alert stays open on the Alerts page)"
                  className="px-3 py-1.5 rounded-lg text-sm font-bold text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-white/5 hover:bg-neutral-200 dark:hover:bg-white/10 cursor-pointer">
                  Dismiss
                </button>
              </div>
            )}
            {/* how long until a warning / information pop-up goes by itself */}
            {life && (
              <div className="h-1 bg-neutral-100 dark:bg-white/5">
                <div className={`h-full ${warn ? 'bg-amber-400' : 'bg-[#4D8DBA]'}`} style={{ animation: `alertToastLife ${life}ms linear forwards` }} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
